import { Card } from "@/components/ui/card";

/**
 * MatchCardSkeleton
 * -----------------------------------------------------------------------------
 * Structural placeholder for `ResultCard` shown while `/advanced/match`
 * is still returning. Mirrors the real card's layout (title block, overall
 * match badge, 5 layer rows, addons area, action buttons) so the moment
 * data arrives, cards paint in place with zero layout shift.
 *
 * Shimmer via a global keyframe (see `shimmer` in `index.css`). Falls back
 * to `animate-pulse` when the keyframe isn't available, since Tailwind's
 * pulse is universally supported.
 */
function Bar({ w = "w-24", h = "h-3", className = "" }) {
  return (
    <div
      className={`${w} ${h} rounded skeleton-bar ${className}`}
      aria-hidden="true"
    />
  );
}

function LayerRowSkeleton() {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <Bar w="w-16" h="h-3" />
      <Bar w="w-10" h="h-3" />
      <Bar w="w-full max-w-[280px]" h="h-3" className="flex-1" />
    </div>
  );
}

export default function MatchCardSkeleton({ testId = "match-card-skeleton" }) {
  return (
    <Card
      data-testid={testId}
      className="p-6 border-stone-200 bg-white"
      aria-hidden="true"
    >
      <div className="flex items-baseline justify-between">
        <div className="flex-1 min-w-0 space-y-2">
          <Bar w="w-40" h="h-2.5" />
          <Bar w="w-2/3" h="h-6" />
          <Bar w="w-1/3" h="h-3" />
          <div className="flex gap-3 mt-2">
            <Bar w="w-20" h="h-2.5" />
            <Bar w="w-24" h="h-2.5" />
            <Bar w="w-28" h="h-2.5" />
          </div>
        </div>
        <div className="text-right shrink-0 space-y-1.5">
          <Bar w="w-20" h="h-10" className="ml-auto" />
          <Bar w="w-24" h="h-2.5" className="ml-auto" />
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <Bar w="w-24" h="h-6" />
        <Bar w="w-20" h="h-6" />
      </div>

      <div className="mt-4 border-t border-stone-100 pt-3 space-y-1">
        <LayerRowSkeleton />
        <LayerRowSkeleton />
        <LayerRowSkeleton />
        <LayerRowSkeleton />
        <LayerRowSkeleton />
      </div>

      <div className="mt-5 pt-4 border-t border-stone-100 flex gap-2">
        <Bar w="w-32" h="h-8" />
        <Bar w="w-32" h="h-8" />
      </div>
    </Card>
  );
}


/**
 * MatchResultsSkeleton
 * A 2-column grid of skeleton cards mirroring the eventual results layout.
 * The count matches the smallest realistic set (4) so the grid never feels
 * sparse on desktop.
 */
export function MatchResultsSkeleton({ count = 4 }) {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-2 gap-4"
      data-testid="match-results-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading match results"
    >
      {Array.from({ length: count }, (_, i) => (
        <MatchCardSkeleton key={i} testId={`match-card-skeleton-${i}`} />
      ))}
    </div>
  );
}
